import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui";
import { useIsMobile } from "@/hooks/use-mobile";
import {
  useBulkDownloadGlMaster,
  useCommitBulkUploadGlMasterMutation,
  useGetCommunities,
  useGetGlMasterData,
  useUpdateGlMasterStatusMutation,
  useValidateBulkUploadGlMasterMutation,
} from "@/services";
import { Check, Edit, MoreVertical, ShieldCheck, ShieldOff, X } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "@/components/Toast";
import { useQueryClient } from "@tanstack/react-query";
import { accountCategoryOptions, ROLE_BOM, subledgerOptions} from "@/lib/utils";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@/redux/store";
import { setCommunityData } from "@/redux/slices";

const useGLSetup = () => {
  const communityData = useSelector((state: RootState) => state.currentData.communityData);
  const masterDataPermission = useSelector((state: RootState) => state.currentData.masterDataPermission);
  const id = communityData?.communityId;
  const isMobile = useIsMobile();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const [globalFilter, setGlobalFilter] = useState("");
  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const [rowSelected, setRowSelected] = useState<any>(null);
  const [isView, setIsView] = useState<boolean>(false);
  const [activeOnly, setActiveOnly] = useState<boolean>(true);
  const [showBulkDialog, setShowBulkDialog] = useState<boolean>(false);
  const [file, setFile] = useState<any>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const currentRole = useSelector((state: RootState) => state.currentData.currentRole);
  const isCamBom = location.pathname.startsWith("/cam") || location.pathname.startsWith("/bom") || (location.pathname.startsWith("/owner") && currentRole == ROLE_BOM);

  const hasAddPermission = useMemo(() => isCamBom ? masterDataPermission?.add : true, [masterDataPermission]);
  const hasUpdatePermission = useMemo(() => isCamBom ? masterDataPermission?.update : true, [masterDataPermission]);

  const { data: communityList, isLoading: isCommunityLoading } =
    useGetCommunities({});

  const { data: glData, isLoading: isLoadingGlMaster } = useGetGlMasterData({
    communityId: id as string,
  });

  const { mutate: updateStatus, isPending: isUpdateStatusPending } =
    useUpdateGlMasterStatusMutation({
      onError: (error?: any) => {
        toast.error(
          typeof error?.response?.data?.message === "string"
            ? error?.response?.data?.message
            : "Something went wrong"
        );
        return error?.response?.data?.message;
      },
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ["gl-master-data", id],
        });
        toast.success("Status Updated Successfully");
      },
    });

  const {
    data: bulkDownload,
    refetch: refetchBulkDownload,
    isRefetching: isBulkDownloading,
  } = useBulkDownloadGlMaster({
    communityId: id as string,
    queryParams: {
      enabled: false,
    },
  });

  useEffect(() => {
    if (bulkDownload) {
      try {
        const response = bulkDownload;
        const blob = new Blob([response.data], {
          type: response.headers["content-type"],
        });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;

        let downloadFileName = `${id}-gl-masters.xlsx`;
        const disposition = response.headers["content-disposition"];
        if (disposition && disposition.includes("filename=")) {
          const match = disposition.match(/filename="?(.+?)"?($|;)/);
          if (match?.[1]) {
            downloadFileName = match[1];
          }
        }

        link.setAttribute("download", downloadFileName);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } catch (error) {
        toast.error("There is an issue while downloading the file");
      } finally {
        queryClient.removeQueries({ queryKey: ["bulk-download-gl-master", id] });
      }
    }
  }, [bulkDownload, id]);

  const { mutate: commitUpload, isPending: isCommitPending } =
    useCommitBulkUploadGlMasterMutation({
      onError: (error?: any) => {
        toast.error(
          typeof error?.response?.data?.message === "string"
            ? error?.response?.data?.message
            : "Something went wrong"
        );
        return error?.response?.data?.message;
      },
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ["gl-master-data", id],
        });
        setShowBulkDialog(false);
        setFile(null);
        toast.success("GL Master Uploaded Successfully");
      },
    });

  const { mutate: validateUpload, isPending: isValidatePending } =
    useValidateBulkUploadGlMasterMutation({
      onError: (error?: any) => {
        toast.error(
          typeof error?.response?.data?.message === "string"
            ? error?.response?.data?.message
            : "Something went wrong"
        );
        return error?.response?.data?.message;
      },
      onSuccess: (data: any) => {
        const attachmentId = data?.data?.data?.attachmentId;
        if (attachmentId) {
          commitUpload({
            communityId: id as string,
            attachmentId,
            data: {
              includeWarnings: true,
            },
          });
        }
      },
    });

  // Define Table Columns
  const columns = [
    {
      accessorKey: "glAccountNumber",
      header: (
        <div className=" w-full">
          Account
          <br /> No.
        </div>
      ),
      accessorFn: (row: any) => String(row.glAccountNumber),
    },
    {
      accessorKey: "accountShortDesc",
      header: (
        <div className=" w-full">
          Account Short <br /> Description
        </div>
      ),
      accessorFn: (row: any) => String(row?.accountShortDesc || ""),
    },
    {
      accessorKey: "systemAccountCode",
      header: (
        <div className=" w-full">
          Virtual <br /> Account No.
        </div>
      ),
      accessorFn: (row: any) => String(row?.systemAccountCode || ""),
    },
    {
      accessorKey: "alternativeAccount",
      header: (
        <div className=" w-full">
          Alternate <br /> Account No.
        </div>
      ),
      accessorFn: (row: any) => String(row?.alternativeAccount || ""),
    },
    {
      accessorKey: "accountCategory",
      header: (
        <div className="w-full">
          Account <br /> Category
        </div>
      ),
      accessorFn: (row: any) =>
        row?.accountCategory
          ? accountCategoryOptions.find(
              (val) => val.value == row?.accountCategory
            )?.label
          : "",
    },
    {
      accessorKey: "reconAccount",
      header: (
        <div className=" w-full">
          Recon <br /> Account
        </div>
      ),
      cell: (row: any) =>
        row?.row?.original?.reconAccount ? (
          <Check className="w-4 h-4 text-green-600 stroke-[5px]" />
        ) : (
          <X className="w-4 h-4 text-red-600 stroke-[5px]" />
        ),
    },
    {
      accessorKey: "clearingAccount",
      header: (
        <div className=" w-full">
          Clearing <br /> Account
        </div>
      ),
      cell: (row: any) =>
        row?.row?.original?.clearingAccount ? (
          <Check className="w-4 h-4 text-green-600 stroke-[5px]" />
        ) : (
          <X className="w-4 h-4 text-red-600 stroke-[5px]" />
        ),
    },
    {
      accessorKey: "noDirectPosting",
      header: (
        <div className=" w-full">
          No Direct <br /> Posting
        </div>
      ),
      cell: (row: any) =>
        row?.row?.original?.noDirectPosting ? (
          <Check className="w-4 h-4 text-green-600 stroke-[5px]" />
        ) : (
          <X className="w-4 h-4 text-red-600 stroke-[5px]" />
        ),
    },
    {
      accessorKey: "subledger",
      header: <div className=" w-full">Subledger</div>,
      accessorFn: (row: any) =>
        row?.subledger
          ? subledgerOptions?.find(
              (val) => val.value?.toLowerCase() == row?.subledger?.toLowerCase()
            )?.label
          : "",
    },
    // {
    //   accessorKey: "subledgerType",
    //   header: (
    //     <div className=" w-full">
    //       Subledger <br /> Type
    //     </div>
    //   ),
    //   accessorFn: (row: any) =>
    //     row?.subledgerType
    //       ? subledgerTypeOptions?.find(
    //           (val) =>
    //             val.value?.toLowerCase() == row?.subledgerType?.toLowerCase()
    //         )?.label
    //       : "",
    // },
    ...(hasUpdatePermission ? [{
      id: "actions",
      header: "Actions",
      cell: (row: any) => {
        return (
          <DropdownMenu>
            <DropdownMenuTrigger>
              <MoreVertical className="w-5 h-5 cursor-pointer" />
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              className="bg-white border-0 shadow-md"
            >
              <DropdownMenuItem
                className="flex items-center gap-2"
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  setIsView(false);
                  setOpenDialog(true);
                  setRowSelected(row.row.original);
                }}
              >
                <Edit className="w-4 h-4" />
                Edit
              </DropdownMenuItem>
              <DropdownMenuItem
                className="flex items-center gap-2"
                disabled={isUpdateStatusPending}
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  updateStatus({
                    communityId: id as string,
                    id: row?.row?.original?.glMasterId,
                    active: !row?.row?.original?.active,
                  });
                }}
              >
                {row?.row?.original?.active ? <>
                  <ShieldOff className="w-4 h-4" />
                  Inactive
                </> : <>
                  <ShieldCheck className="w-4 h-4" />
                  Active
                </>}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        );
      },
    },
  ] : []),
];

  const onAdd = () => {
    setIsView(false);
    setOpenDialog(true);
    setRowSelected(null);
  };

  const onRowClick = (row: any) => {
    setIsView(true);
    setRowSelected(row.original);
    setOpenDialog(true);
  };

  const onCloseDialog = () => {
    setOpenDialog(false);
  };

  const onBulkDownload = () => {
    refetchBulkDownload();
  };

  const onBulkUpload = () => {
    setShowBulkDialog(true);
  };

  const onUpload = () => {
    if (!file) {
      toast.error("Please select file");
      return;
    }

    validateUpload({
      communityId: id as string,
      data: {
        file,
      },
    });
  };

  const onCommunityChange = (e: any) => {
    const details = communityList?.data?.find(
      (data: any) => data.communityId == e
    );
    dispatch(setCommunityData(details));
  };
  
  const bulkUploadTitle = `Bulk Upload GL Master`;
  const rows = useMemo(() => glData?.data?.filter((data: any) => data?.active == activeOnly) || [], [glData, activeOnly]);

  return {
    setGlobalFilter,
    globalFilter,
    isMobile,
    onAdd,
    isLoadingGlMaster,
    columns,
    glData,
    id,
    onRowClick,
    setOpenDialog,
    onCloseDialog,
    openDialog,
    rowSelected,
    isView,
    showBulkDialog,
    setShowBulkDialog,
    file,
    fileInputRef,
    setFile,
    onUpload,
    isBulkDownloading,
    onBulkDownload,
    onBulkUpload,
    isUploadPending: isValidatePending || isCommitPending,
    isCommunityLoading,
    communityList,
    onCommunityChange,
    communityId: id,
    hasAddPermission,
    activeOnly,
    setActiveOnly,
    bulkUploadTitle,
    rows
  };
};

export default useGLSetup;
